import * as React from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { EventTileFromRow, LogoBar } from 'shared-front-end';
import { MarketingHero, MarketingLink, MarketingPage } from '../website-marketing/MarketingShell';
import { HospitalsHero } from '../website-marketing/HospitalsHero';
import { marketingPaths } from '../website-marketing/marketingData';
import {
  PaginatorAndListSettings,
  ParallelLoad,
  useQueryOptionsFromSearchParams,
  useCacheQuery,
} from 'gossamer-front-end';
import type { QueryOptions } from 'gossamer-unified';
import { EventExtendedView } from 'cb-model';
import {
  emptyWebsiteEventFilterValues,
  makeWebsiteEventQueryFilters,
  removeEventFilters,
  WebsiteEventFilterSidebar,
  WebsiteEventFilterValues,
} from '../website-events/WebsiteEventFilters';
import classes from './Home.module.css';

export const Home = (): React.JSX.Element => {
  const now = React.useRef(new Date().toISOString());

  const [queryOptions, setQueryOptions] = useQueryOptionsFromSearchParams(EventExtendedView, {
    where: {
      and: [{ fieldId: 'end_at', operator: 'gt', value: now.current, display: 'hidden' }],
    },
    order: [['start_at', 'ASC']],
    limit: 24,
  });

  const [eventFilters, setEventFilters] = React.useState<WebsiteEventFilterValues>(emptyWebsiteEventFilterValues);

  const filteredQueryOptions = React.useMemo<QueryOptions>(
    () => ({
      ...queryOptions,
      where: {
        and: [
          ...removeEventFilters(queryOptions.where?.and),
          ...makeWebsiteEventQueryFilters(eventFilters, { includeFutureDefault: false }),
        ],
      },
    }),
    [eventFilters, queryOptions],
  );
  const gCacheQueryItem = useCacheQuery(EventExtendedView, filteredQueryOptions);

  const updateEventFilters = (nextFilters: WebsiteEventFilterValues) => {
    setEventFilters(nextFilters);
    setQueryOptions({
      ...queryOptions,
      offset: 0,
      where: { and: removeEventFilters(queryOptions.where?.and) },
    });
  };

  const updateEventQueryOptions = (nextQueryOptions: QueryOptions) => {
    setQueryOptions({
      ...nextQueryOptions,
      where: { and: removeEventFilters(nextQueryOptions.where?.and) },
    });
  };

  return (
    <div>
      <MarketingPage>
        <MarketingHero
          eyebrow={
            <>
              <img src="/assets/icons/for-nhs-hospitals-and-trusts.svg" alt="" /> For NHS hospitals and medical
              education teams
            </>
          }
          title="Medical education that runs itself."
          summary="Careerbook automates timetabling, attendance, feedback and compliance reporting for undergraduate and postgraduate teaching — giving your team back more than 90% of its admin time."
          actions={
            <>
              <MarketingLink href={marketingPaths.contact}>Book a demo</MarketingLink>
              <MarketingLink href="/website/hospitals-and-trusts#platform" secondary>
                See the platform
              </MarketingLink>
            </>
          }
          proof={
            <Stack direction="horizontal" gap={2}>
              <img src="/assets/icons/trust-circles.svg" />
              <span>
                Trusted by <b>270+ healthcare organisations</b> and <b>175,000+ clinicians and learners</b>
              </span>
            </Stack>
          }
          visual={<HospitalsHero registerCount="30 of 32 signed in" registerPct={94} />}
        />
      </MarketingPage>

      <LogoBar />

      <br />

      <div className={classes.EventLayout}>
        <WebsiteEventFilterSidebar
          className={classes.FiltersPanel}
          values={eventFilters}
          onChange={updateEventFilters}
        />
        <div className={classes.EventMain}>
          <ParallelLoad responses={[gCacheQueryItem]}>
            <PaginatorAndListSettings
              entity={EventExtendedView}
              gCacheQuery={gCacheQueryItem}
              queryOptions={filteredQueryOptions}
              showItemCount={true}
              render={(_) => (
                <Row xs={1} md={2} lg={3} className="g-3">
                  {gCacheQueryItem.data?.map((item) => (
                    <Col key={item.id}>
                      <EventTileFromRow item={item} />
                    </Col>
                  ))}
                </Row>
              )}
              setQueryOptions={updateEventQueryOptions}
              showSettingsControl={false}
            />
          </ParallelLoad>
        </div>
      </div>
    </div>
  );
};
